import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Button, 
  Grid, MenuItem, Divider, IconButton, Autocomplete, Alert,
  Switch,
} from '@mui/material';
import { ArrowBack, Save, Inventory, Notes, Warning, Delete, CheckCircle, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { authService } from '../../services/authService';
import { ROLES } from '../../constants/roles';
import { useNotification } from '../../contexts/NotificationContext';
import { Category } from '../../types/api';

interface ExistingItem {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
}

interface ItemFormProps {
  mode?: 'create' | 'edit';
}

const ItemForm = ({ mode = 'create' }: ItemFormProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError, showWarning, confirm, alert } = useNotification();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    category_id: 0,
    units_per_carton: 1,
    min_stock: 0,
    remark: '',
  });

  const currentUser = authService.getUser();
  const isAdmin = currentUser?.role === ROLES.ADMIN;

  // Categories from API
  const [categories, setCategories] = useState<Category[]>([]);

  // For duplicate detection
  const [allItems, setAllItems] = useState<ExistingItem[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Fetch categories from API
  useEffect(() => {
    api.get('/api/categories')
      .then(res => {
        const cats = res.data.categories || [];
        setCategories(cats);
      })
      .catch(err => console.error('Error fetching categories:', err));
  }, []);

  // Fetch all items for duplicate check
  useEffect(() => {
    api.get('/api/items')
      .then(res => {
        const items = res.data.items || [];
        setAllItems(items.map((item: any) => ({
          id: item.id,
          name: item.name,
          category_id: item.category_id,
          category_name: item.category_name,
        })));
      })
      .catch(err => console.error('Error fetching items:', err));
  }, []);

  useEffect(() => {
    if (mode === 'edit' && id) {
      api.get(`/api/items/${id}`)
        .then(res => {
          const item = res.data;
          setFormData({
            name: item.name || '',
            category_id: item.category_id || 0,
            units_per_carton: item.units_per_carton || 1,
            min_stock: item.min_stock || 0,
            remark: item.remark || '',
          });
          setIsEnabled(item.is_enabled ?? true);
        })
        .catch(err => {
          console.error(err);
          showError('Failed to load item details');
          navigate(-1);
        });
    }
  }, [mode, id, navigate]);

  // Check for exact duplicate when name changes
  useEffect(() => {
    if (mode === 'create' && formData.name.trim()) {
      const searchName = formData.name.toLowerCase().trim();
      const exactMatch = allItems.some(item => 
        item.name.toLowerCase() === searchName
      );
      setShowDuplicateWarning(exactMatch);
    } else {
      setShowDuplicateWarning(false);
    }
  }, [formData.name, allItems, mode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ 
      ...formData, 
      [name]: (name === 'min_stock' || name === 'units_per_carton') ? Number(value) : value 
    });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ 
      ...formData, 
      category_id: Number(e.target.value),
      // Reset name when category changes in create mode
      ...(mode === 'create' ? { name: '' } : {})
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.category_id) {
      showWarning('Category is required');
      return;
    }
    if (!formData.name.trim()) {
      showWarning('Item name is required');
      return;
    }
    if (showDuplicateWarning && mode === 'create') {
      showWarning('An item with this name already exists. Please use a different name.');
      return;
    }
    if (!formData.units_per_carton || formData.units_per_carton < 1) {
      showWarning('Units per Carton must be at least 1');
      return;
    }

    const payload = {
      name: formData.name,
      category_id: formData.category_id,
      units_per_carton: formData.units_per_carton,
      min_stock: formData.min_stock,
      remark: formData.remark || undefined,
      is_enabled: isEnabled,
    };

    setLoading(true);
    try {
      if (mode === 'edit' && id) {
        await api.put(`/api/items/${id}`, payload);
        showSuccess('Item updated successfully!');
        navigate(-1);
      } else {
        const response = await api.post('/api/items', payload);
        const itemId = response.data?.id;
        // Reset form
        setFormData({
          name: '',
          category_id: 0,
          units_per_carton: 1,
          min_stock: 0,
          remark: '',
        });
        setIsEnabled(true);
        setShowDuplicateWarning(false);
        // Show success dialog
        await alert({
          title: 'Item Created',
          message: `Item ${response.data.name}(#${response.data.id}) created successfully!`,
          icon: 'success',
        });
      }
    } catch (err) {
      showError('Error saving item. Please check inputs.');
    } finally {
      setLoading(false);
    }
  };

  // Get existing item names in selected category for autocomplete suggestions
  const itemNamesInCategory = allItems
    .filter(item => item.category_id === formData.category_id)
    .map(item => item.name);

  const handleDelete = async () => {
    if (!id) return;
    
    const confirmed = await confirm({
      title: 'Delete Item',
      message: 'Are you sure you want to remove this item? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmColor: 'error',
      icon: 'warning',
    });
    if (!confirmed) return;

    setDeleting(true);
    try {
      await api.delete(`/api/items/${id}`);
      showSuccess('Item deleted successfully!');
      navigate('/items');
    } catch (err) {
      console.error('Error deleting item:', err);
      showError('Failed to delete item. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {mode === 'edit' ? 'Edit Item' : 'Add New Item'}
        </Typography>
      </Box>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 4, borderRadius: 3 }}>
        {/* Enable/Disable Toggle - Only in Edit Mode */}
        {mode === 'edit' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: isEnabled ? 'success.50' : 'grey.100',
              border: '1px solid',
              borderColor: isEnabled ? 'success.200' : 'grey.300',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isEnabled ? (
                <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
              ) : (
                <Cancel sx={{ color: 'text.disabled', fontSize: 28 }} />
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {isEnabled ? 'Item Active' : 'Item Inactive'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isEnabled ? 'This item is visible and available' : 'This item is hidden from listings'}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              color="success"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'success.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'success.main',
                },
              }}
            />
          </Box>
        )}

        {/* General Information Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Inventory color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">General Information</Typography>
        </Box>
        
        <Grid container spacing={3}>
          {/* Category - First */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              label="Category"
              name="category_id"
              required
              value={formData.category_id || ''}
              onChange={handleCategoryChange}
              helperText={mode === 'create' ? "Select category first" : undefined}
            >
              {categories.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Item Name - Searchable with duplicate detection */}
          <Grid size={{ xs: 12, md: 6 }}>
            {mode === 'create' ? (
              <Autocomplete
                freeSolo
                options={itemNamesInCategory}
                value={formData.name}
                disabled={!formData.category_id}
                onInputChange={(_, newValue) => {
                  setFormData({ ...formData, name: newValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Item Name"
                    name="name"
                    required
                    placeholder={formData.category_id ? "Type to search existing items..." : "Select category first"}
                    error={showDuplicateWarning}
                    helperText={
                      !formData.category_id 
                        ? "Select a category first" 
                        : showDuplicateWarning 
                          ? "This item name already exists!" 
                          : "Type to check for existing items"
                    }
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span>{option}</span>
                  </li>
                )}
              />
            ) : (
              <TextField
                fullWidth
                label="Item Name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter item name"
              />
            )}
          </Grid>

          {/* Exact duplicate error */}
          {mode === 'create' && showDuplicateWarning && (
            <Grid size={12}>
              <Alert severity="error">
                An item with the name "<strong>{formData.name}</strong>" already exists. 
                Please use a different name to avoid duplicates.
              </Alert>
            </Grid>
          )}

          {/* Units per Carton */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Units per Carton"
              name="units_per_carton"
              required
              value={formData.units_per_carton}
              onChange={handleChange}
              helperText="Number of units in one carton"
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Minimum Stock */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Stock Threshold"
              name="min_stock"
              helperText="Alert when stock falls below this level"
              value={formData.min_stock}
              onChange={handleChange}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Note/Specification Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Notes color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Note / Specification</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            (Optional)
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Note / Specification"
              name="remark"
              value={formData.remark}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="Add any notes or specifications about this item..."
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {mode === 'edit' && isAdmin && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
              disabled={loading || deleting}
            >
              {deleting ? 'Removing...' : 'Remove Item'}
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading || deleting}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            size="large"
            startIcon={<Save />}
            disabled={loading || deleting || (mode === 'create' && showDuplicateWarning)}
          >
            {loading ? 'Saving...' : mode === 'edit' ? 'Update Item' : 'Create Item'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ItemForm;